import React, { useState, useEffect } from 'react';
import { CardSearchBar } from '../filters/CardSearchBar';
import { X, Search, Repeat, Heart } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useCollection } from '../../hooks/useCollection';
import { useI18n } from '../../contexts/I18nContext';

interface CardSuggestion {
  id: number;
  card_fr: string;
  set: string;
  rarity: string;
  image: string;
}

interface TradeFiltersProps {
  filters: {
    search: string;
    wantedCardFilter: 'all' | 'owned' | 'tradeable' | 'tradable';
    proposedCardsFilter: 'all' | 'needAtLeastOne' | 'hasTradeableCard' | 'wanted';
    wantedCardId?: number;
    proposedCardId?: number;
    rarity: string | null;
  };
  onFilterChange: (filters: TradeFiltersProps['filters']) => void;
  onSearch: (search: string) => void;
  isAuthenticated: boolean;
  isProposedCardsSection?: boolean;
  rarities: string[];
}

type FilterButtonProps = {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
};

const FilterButton = ({ active, onClick, children }: FilterButtonProps) => (
  <button
    onClick={onClick}
    className={`
      px-3 py-1.5 text-sm font-medium rounded transition-colors
      ${active 
        ? 'bg-gray-900 text-white hover:bg-gray-800' 
        : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
      }
    `}
  >
    {children}
  </button>
);

export const TradeFilters = ({ 
  filters, 
  onFilterChange, 
  onSearch, 
  isAuthenticated,
  isProposedCardsSection = false,
  rarities = []
}: TradeFiltersProps) => {
  const { t } = useI18n();
  const { collection } = useCollection();
  const [wantedCardSearch, setWantedCardSearch] = useState('');
  const [proposedCardSearch, setProposedCardSearch] = useState('');
  const [selectedWantedCard, setSelectedWantedCard] = useState<CardSuggestion | null>(null);
  const [selectedProposedCard, setSelectedProposedCard] = useState<CardSuggestion | null>(null);
  const [showWantedCardSearch, setShowWantedCardSearch] = useState(false);
  const [showProposedCardSearch, setShowProposedCardSearch] = useState(false);

  // Charger les cartes sélectionnées au montage
  useEffect(() => {
    const loadSelectedCards = async () => {
      if (filters.wantedCardId) {
        const { data } = await supabase
          .from('cards')
          .select('id, card_fr, set, rarity, image')
          .eq('id', filters.wantedCardId)
          .single();
        if (data) {
          setSelectedWantedCard(data);
          setWantedCardSearch(data.card_fr);
          setShowWantedCardSearch(true);
        }
      }

      if (filters.proposedCardId) {
        const { data } = await supabase
          .from('cards')
          .select('id, card_fr, set, rarity, image')
          .eq('id', filters.proposedCardId)
          .single();
        if (data) {
          setSelectedProposedCard(data);
          setProposedCardSearch(data.card_fr);
          setShowProposedCardSearch(true);
        }
      }
    };

    loadSelectedCards();
  }, [filters.wantedCardId, filters.proposedCardId]);

  const handleWantedCardReset = () => {
    setWantedCardSearch('');
    setSelectedWantedCard(null);
    setShowWantedCardSearch(false);
    onFilterChange({ ...filters, wantedCardId: undefined });
  };

  const handleProposedCardReset = () => {
    setProposedCardSearch('');
    setSelectedProposedCard(null);
    setShowProposedCardSearch(false);
    onFilterChange({ ...filters, proposedCardId: undefined });
  };

  const hasCard = (cardId: number) => {
    return collection[cardId]?.quantity > 0;
  };

  return (
    <div className="bg-white shadow-sm rounded-lg -mx-4 sm:mx-0">
      <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-100">
        {/* Section Carte recherchée par le créateur */}
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-medium text-gray-900">{t('trades.filters.wanted_card.title')}</h3>
              <p className="text-xs text-gray-500 mt-1">
                {t('trades.filters.wanted_card.description')}
              </p>
            </div>
            {showWantedCardSearch && (
              <button
                onClick={handleWantedCardReset}
                className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
              >
                <X className="w-3 h-3" />
                {t('common.reset')}
              </button>
            )}
          </div>

          <div className="space-y-3">
            {/* Barre de recherche */}
            <CardSearchBar
              value={wantedCardSearch}
              onChange={setWantedCardSearch}
              onReset={handleWantedCardReset}
              onSearch={() => {}}
              onCardSelect={(card) => {
                setWantedCardSearch(card.card_fr);
                setSelectedWantedCard(card);
                onFilterChange({ ...filters, wantedCardId: card.id });
              }}
              placeholder={t('trades.filters.search_card_placeholder')}
              selectedCard={selectedWantedCard}
            />

            {/* Filtres de possession */}
            {isAuthenticated && (
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => onFilterChange({ ...filters, wantedCardFilter: 'all' })}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                    filters.wantedCardFilter === 'all'
                      ? 'bg-gray-900 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {t('trades.filters.show_all')}
                </button>
                <button
                  onClick={() => onFilterChange({ ...filters, wantedCardFilter: 'owned' })}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                    filters.wantedCardFilter === 'owned'
                      ? 'bg-green-600 text-white'
                      : 'bg-green-50 text-green-700 hover:bg-green-100'
                  }`}
                >
                  {t('trades.filters.owned')}
                </button>
                <button
                  onClick={() => onFilterChange({ ...filters, wantedCardFilter: 'tradeable' })}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                    filters.wantedCardFilter === 'tradeable'
                      ? 'bg-blue-600 text-white'
                      : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                  }`}
                >
                  {t('trades.filters.doubles')}
                </button>
                <button
                  onClick={() => onFilterChange({ ...filters, wantedCardFilter: 'tradable' })}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors flex items-center gap-2 ${
                    filters.wantedCardFilter === 'tradable'
                      ? 'bg-green-600 text-white'
                      : 'bg-green-50 text-green-700 hover:bg-green-100'
                  }`}
                >
                  <Repeat className="w-3 h-3" />
                  {t('trades.filters.tradable')}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Section Cartes proposées par le créateur */}
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-medium text-gray-900">{t('trades.filters.proposed_cards.title')}</h3>
              <p className="text-xs text-gray-500 mt-1">
                {t('trades.filters.proposed_cards.description')}
              </p>
            </div>
            {showProposedCardSearch && (
              <button
                onClick={handleProposedCardReset}
                className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
              >
                <X className="w-3 h-3" />
                {t('common.reset')}
              </button>
            )}
          </div>

          <div className="space-y-3">
            {/* Barre de recherche */}
            <CardSearchBar
              value={proposedCardSearch}
              onChange={setProposedCardSearch}
              onReset={handleProposedCardReset}
              onSearch={() => {}}
              onCardSelect={(card) => {
                setProposedCardSearch(card.card_fr);
                setSelectedProposedCard(card);
                onFilterChange({ ...filters, proposedCardId: card.id });
              }}
              placeholder={t('trades.filters.search_card_placeholder')}
              selectedCard={selectedProposedCard}
            />

            {/* Filtres de possession */}
            {isAuthenticated && (
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => onFilterChange({ ...filters, proposedCardsFilter: 'all' })}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                    filters.proposedCardsFilter === 'all'
                      ? 'bg-gray-900 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {t('trades.filters.show_all')}
                </button>
                <button
                  onClick={() => onFilterChange({ ...filters, proposedCardsFilter: 'needAtLeastOne' })}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                    filters.proposedCardsFilter === 'needAtLeastOne'
                      ? 'bg-orange-600 text-white'
                      : 'bg-orange-50 text-orange-700 hover:bg-orange-100'
                  }`}
                >
                  {t('trades.filters.missing')}
                </button>
                <button
                  onClick={() => onFilterChange({ ...filters, proposedCardsFilter: 'hasTradeableCard' })}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                    filters.proposedCardsFilter === 'hasTradeableCard'
                      ? 'bg-purple-600 text-white'
                      : 'bg-purple-50 text-purple-700 hover:bg-purple-100'
                  }`}
                >
                  {t('trades.filters.few_owned')}
                </button>
                <button
                  onClick={() => onFilterChange({ ...filters, proposedCardsFilter: 'wanted' })}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors flex items-center gap-2 ${
                    filters.proposedCardsFilter === 'wanted'
                      ? 'bg-red-600 text-white'
                      : 'bg-red-50 text-red-700 hover:bg-red-100'
                  }`}
                >
                  <Heart className="w-3 h-3" fill={filters.proposedCardsFilter === 'wanted' ? "currentColor" : "none"} />
                  {t('trades.filters.wanted')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Section Rareté */}
      <div className="border-t border-gray-100 p-4">
        <h3 className="text-sm font-medium text-gray-900 mb-3">
          {t('trades.filters.filter_by_rarity')}
        </h3>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => onFilterChange({ ...filters, rarity: null })}
            className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
              filters.rarity === null
                ? 'bg-gray-900 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {t('trades.filters.all_rarities')}
          </button>
          {rarities.map((rarity) => (
            <button
              key={rarity}
              onClick={() => onFilterChange({ ...filters, rarity })}
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                filters.rarity === rarity
                  ? 'bg-indigo-600 text-white'
                  : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
              }`}
            >
              {rarity}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}; 